import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ChevronDown, ChevronUp, MessageSquare, Eye, BarChart3, Bell, Lock, User } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';

const STEPS = [
  { icon: Lock, title: 'Signing the Consent Form', content: 'You received an email asking you to review and sign a consent form for your child to use YAS Connect. Click the link in the email, review the terms, type your name to sign, and submit. Once signed, your child gets full access to the platform.' },
  { icon: User, title: 'Logging Into Your Parent Dashboard', content: 'After signing consent, you receive a separate email with your own login credentials. Go to the YAS Connect URL, enter your email and password, and you will land on your Parent Dashboard. Change your password on first login.' },
  { icon: Eye, title: 'Viewing Your Child\'s Progress', content: 'Your Parent Dashboard shows your child\'s key information: their points earned, goals set and completed, workshops they have finished, check-in status, and their mentor\'s name. This gives you a snapshot of how engaged they are in the program.' },
  { icon: BarChart3, title: 'Understanding Their Development', content: 'You can see your child\'s interests, career aspirations, and skills they are building. The platform also provides AI-generated insights about their strengths and areas where they could use more support. Use this information to have meaningful conversations with your child about their future.' },
  { icon: MessageSquare, title: 'Messaging Their Mentor', content: 'From your dashboard, tap the Messages section to send a message directly to your child\'s program mentor. You can ask about their progress, share concerns, or coordinate on anything related to the program. The mentor receives an email notification and will respond through the platform.' },
  { icon: Bell, title: 'Getting Notifications', content: 'You will receive email notifications when: your child\'s mentor sends you a message, important program announcements are posted, or if there are events or surveys that require your attention. Make sure to check your email regularly.' },
];

const FAQ = [
  { q: 'Can I see my child\'s private messages with their mentor?', a: 'No. Private conversations between your child and their mentor are confidential. This is to ensure your child feels safe being honest with their mentor. You can, however, message the mentor directly yourself.' },
  { q: 'Can I see what my child writes in their reflections?', a: 'No. Weekly reflections and workshop journal entries are private between your child and their mentor. This encourages honest self-reflection.' },
  { q: 'What if I have a concern about the program?', a: 'Message your child\'s mentor directly through the platform, or contact the program office. Their contact information is on your dashboard.' },
  { q: 'What do I do if I forget my password?', a: 'On the login page, click "Forgot Password" and enter your email. You will receive a reset link.' },
  { q: 'Can I access this on my phone?', a: 'Yes! YAS Connect works on any phone, tablet, or computer. Just open the URL in your browser. You can also add it to your home screen for quick access.' },
];

const ParentHowToPage = () => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState({});
  const [faqExpanded, setFaqExpanded] = useState({});

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F3EF' }}>
      <div style={{ background: 'linear-gradient(135deg, #0A0E1A 0%, #1a1f3a 100%)' }} className="py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <button onClick={() => navigate(-1)} className="p-2 rounded-xl bg-white/10 hover:bg-white/15 mb-4">
            <ArrowLeft className="w-5 h-5 text-white/70" />
          </button>
          <h1 className="text-2xl font-black text-white">Parent Guide</h1>
          <p className="text-sm text-white/60 mt-1">Step-by-step tutorial for using YAS Connect as a parent</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {/* Steps */}
        {STEPS.map((step, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
            <Card className="rounded-2xl overflow-hidden border border-gray-100">
              <button onClick={() => setExpanded(prev => ({ ...prev, [i]: !prev[i] }))}
                className="w-full p-4 flex items-center gap-3 text-left hover:bg-gray-50 transition-all">
                <div className="w-10 h-10 rounded-xl bg-orange-500 text-white flex items-center justify-center font-bold flex-shrink-0">{i + 1}</div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-gray-900">{step.title}</p>
                </div>
                {expanded[i] ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </button>
              {expanded[i] && (
                <div className="px-4 pb-4 ml-13">
                  <div className="p-4 bg-gray-50 rounded-xl ml-12">
                    <p className="text-sm text-gray-600 leading-relaxed">{step.content}</p>
                  </div>
                </div>
              )}
            </Card>
          </motion.div>
        ))}

        {/* FAQ */}
        <h2 className="text-lg font-bold text-gray-900 pt-4">Frequently Asked Questions</h2>
        {FAQ.map((faq, i) => (
          <Card key={i} className="rounded-2xl overflow-hidden border border-gray-100">
            <button onClick={() => setFaqExpanded(prev => ({ ...prev, [i]: !prev[i] }))}
              className="w-full p-4 flex items-center gap-3 text-left hover:bg-gray-50 transition-all">
              <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                <span className="text-blue-500 font-bold text-xs">Q</span>
              </div>
              <p className="text-sm font-medium text-gray-800 flex-1">{faq.q}</p>
              {faqExpanded[i] ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>
            {faqExpanded[i] && (
              <div className="px-4 pb-4 ml-11">
                <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
              </div>
            )}
          </Card>
        ))}

        <div className="text-center py-6">
          <p className="text-xs text-gray-400">Need more help? Email support@yasconnect.co</p>
        </div>
      </div>
    </div>
  );
};

export default ParentHowToPage;
